/**
 * 화면 전용 도구 등록소
 *
 * 왜 필요한가:
 *   AI 는 SP 를 직접 부를 수 있지만, 복잡한 화면(가격/재고 같은)의 저장은 SP 만으로 조립이 안 된다.
 *   예를 들어 투어 재고 저장 SP 의 @선택행사 에는 "자원번호" 가 들어가야 하는데, 이 값은
 *   요금제↔자원 매핑에서 나오는 화면 내부 상태다. 화면조차 이 값이 없으면 저장을 포기한다.
 *   가격 저장의 @가격정보 도 마찬가지로 화면이 조립하는 큰 JSON 이다.
 *
 *   그래서 AI 가 SP 를 손으로 조립하게 두는 대신, 화면이 "이 화면에서 할 수 있는 일" 을 여기에
 *   등록해 두면 AI 가 그걸 부른다. 실제 저장은 화면이 이미 쓰고 있는(=사람이 버튼으로 눌러
 *   검증된) 함수가 그대로 수행한다. 자원번호·가격번호 같은 건 화면이 채운다.
 *
 * 쓰는 쪽(화면):
 *   useScreenTools(() => ({
 *     screen: '가격/재고',
 *     description: '...이 화면이 무엇인지 AI 에게 설명',
 *     state: { 상품번호: 111366, 선택된요금제: '기본' },   // AI 가 상황을 알도록 넘기는 값
 *     actions: [{
 *       name: 'price_stock_apply',
 *       description: '...',
 *       parameters: { ...Gemini 함수 스키마... },
 *       write: true,                       // true 면 실행 전 사용자 확인을 받는다
 *       preview: (args) => ({ ... }),      // 확인 카드에 보여줄 내용
 *       run: async (args) => ({ ... }),    // 실제 수행. 반환값이 AI 에게 돌아간다
 *     }],
 *   }));
 *
 * 한 번에 한 화면만 등록된다. 화면을 옮기면 이전 등록은 자동으로 풀린다.
 */

import { useEffect, useRef } from 'react';

/** 기본 도구 이름과 겹치면 안 된다 */
const RESERVED_NAMES = new Set(['search_procedures', 'run_query', 'run_write']);

/** 현재 등록된 팩토리. 호출 시점에 실행해서 항상 최신 화면 상태를 얻는다. */
let factory = null;

/** 화면이 자기 도구를 등록한다. 반환값을 부르면 등록이 풀린다. */
export function registerScreenTools(nextFactory) {
  factory = nextFactory;
  return () => {
    if (factory === nextFactory) factory = null;
  };
}

/**
 * 지금 화면의 등록 내용을 읽는다.
 * 팩토리가 던지면(렌더 도중 상태가 덜 찼다든지) 조용히 없는 것으로 친다 —
 * 화면 도구 하나 때문에 채팅 전체가 죽으면 안 된다.
 */
function readRegistration() {
  if (!factory) return null;
  try {
    const reg = factory();
    if (!reg || !Array.isArray(reg.actions)) return null;
    return reg;
  } catch (error) {
    try { console.warn('[screenTools] 화면 도구를 읽지 못했습니다', error); } catch (_) {}
    return null;
  }
}

/** 시스템 프롬프트에 넣을 화면 설명. 등록이 없으면 빈 문자열. */
export function describeScreenContext() {
  const reg = readRegistration();
  if (!reg) return '';

  const lines = [`- 화면 이름: ${reg.screen || '알 수 없음'}`];
  if (reg.description) lines.push(`- 이 화면은: ${reg.description}`);
  if (reg.state && typeof reg.state === 'object') {
    for (const [key, value] of Object.entries(reg.state)) {
      if (value === undefined || value === null || value === '') continue;
      const shown = Array.isArray(value) ? value.join(', ') : String(value);
      lines.push(`- ${key}: ${shown}`);
    }
  }

  const usable = validActions(reg);
  if (usable.length) {
    lines.push(
      '',
      '이 화면에는 전용 도구가 있습니다. 아래 도구로 할 수 있는 일이라면 SP(run_query/run_write)를 직접 조립하지 말고',
      '반드시 이 도구를 쓰세요. 화면이 자원번호·가격번호 같은 내부 값을 알아서 채워 줍니다.',
      ...usable.map((a) => `  · ${a.name} — ${a.description}`),
    );
  }
  return lines.join('\n');
}

/** 이름이 성한 액션만 추린다 */
function validActions(reg) {
  return (reg?.actions || []).filter((a) => a && typeof a.name === 'string' && !RESERVED_NAMES.has(a.name));
}

/** Gemini 에 넘길 functionDeclarations. 등록이 없으면 빈 배열. */
export function buildScreenToolDeclarations() {
  const reg = readRegistration();
  if (!reg) return [];
  return validActions(reg).map((a) => ({
    name: a.name,
    description: a.description || '',
    parameters: a.parameters || { type: 'object', properties: {} },
  }));
}

/**
 * 화면이 등록해 둔 state 를 그대로 읽는다.
 * 확인 카드에 "무엇에다 저장하는지"(상품번호·상품명)를 붙일 때 쓴다 —
 * 화면은 이미 그 값을 알고 있으므로 서버에 다시 물어볼 필요가 없다.
 */
export function readScreenState() {
  const reg = readRegistration();
  return reg?.state && typeof reg.state === 'object' ? reg.state : null;
}

/** 이름으로 액션을 찾는다. 없으면 null (= 기본 도구이거나 오타). */
export function findScreenAction(name) {
  const reg = readRegistration();
  if (!reg) return null;
  return validActions(reg).find((a) => a.name === name) || null;
}

/**
 * 화면에서 쓰는 훅.
 * 매 렌더마다 최신 클로저를 ref 에 담고, 등록소에는 "그 ref 를 읽는 팩토리" 를 한 번만 넘긴다.
 * 그래야 등록/해제를 반복하지 않으면서도 AI 가 부를 때 항상 최신 상태를 본다.
 */
export function useScreenTools(build) {
  const ref = useRef(build);
  ref.current = build;
  useEffect(() => registerScreenTools(() => ref.current()), []);
}
